import * as React from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { cn } from '../lib'
import { typographyStyle } from '../lib/catalog'
import { designTokens } from '../lib/design-tokens'

type RadioGroupProps = React.HTMLAttributes<HTMLDivElement>

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} role="radiogroup" className={cn('grid gap-2', className)} {...props} />
  )
)
RadioGroup.displayName = 'RadioGroup'

type RadioGroupItemProps = React.InputHTMLAttributes<HTMLInputElement>

const RadioGroupItem = React.forwardRef<HTMLInputElement, RadioGroupItemProps>(
  ({ className, style, ...props }, ref) => {
    const tokenStyle: CSSProperties & { [key in `--control-${string}`]?: string } = {
      '--control-ring': designTokens.colors.border.inputFocus,
      '--control-ring-offset': designTokens.colors.background.surface,
      width: designTokens.sizes.width.radio,
      height: designTokens.sizes.height.radio,
      borderRadius: designTokens.borders.radius.pill,
      borderWidth: designTokens.borders.widths.radio,
      borderStyle: designTokens.borders.styles.solid,
      borderColor: designTokens.colors.border.radio,
      backgroundColor: designTokens.colors.background.white,
      accentColor: designTokens.colors.background.radioSelected,
    }

    return (
      <input
        ref={ref}
        type="radio"
        className={cn(
          'border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--control-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--control-ring-offset)] disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        style={{ ...tokenStyle, ...style }}
        {...props}
      />
    )
  }
)
RadioGroupItem.displayName = 'RadioGroupItem'

type RadioFieldProps = Omit<RadioGroupItemProps, 'type' | 'children'> & {
  children?: ReactNode
  label?: ReactNode
  className?: string
  inputClassName?: string
  indicatorClassName?: string
  labelClassName?: string
  checkedIcon?: ReactNode
  uncheckedIcon?: ReactNode
  gap?: string
}

const defaultCheckedIcon = (
  <svg
    aria-hidden="true"
    width={designTokens.sizes.width.radio}
    height={designTokens.sizes.height.radio}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="12"
      cy="12"
      r="8"
      stroke={designTokens.colors.border.radio}
      strokeWidth={designTokens.borders.widths.radio}
    />
    <path
      d="M12 17C14.7614 17 17 14.7614 17 12C17 9.23858 14.7614 7 12 7C9.23858 7 7 9.23858 7 12C7 14.7614 9.23858 17 12 17Z"
      fill={designTokens.colors.background.radioSelected}
    />
  </svg>
)

const defaultUncheckedIcon = (
  <svg
    aria-hidden="true"
    width={designTokens.sizes.width.radio}
    height={designTokens.sizes.height.radio}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="12"
      cy="12"
      r="8"
      stroke={designTokens.colors.border.radio}
      strokeWidth={designTokens.borders.widths.radio}
    />
  </svg>
)

const RadioField = React.forwardRef<HTMLInputElement, RadioFieldProps>(
  (
    {
      className,
      inputClassName,
      indicatorClassName,
      labelClassName,
      checkedIcon = defaultCheckedIcon,
      uncheckedIcon = defaultUncheckedIcon,
      gap = designTokens.spacing.xs,
      children,
      label,
      checked,
      defaultChecked,
      disabled,
      onChange,
      ...props
    },
    ref
  ) => {
    const [uncontrolledChecked, setUncontrolledChecked] = React.useState(Boolean(defaultChecked))
    const isControlled = checked !== undefined
    const isChecked = isControlled ? Boolean(checked) : uncontrolledChecked
    const labelContent = label ?? children

    const indicatorStyle: CSSProperties & { [key in `--control-${string}`]?: string } = {
      '--control-ring': designTokens.colors.border.inputFocus,
      '--control-ring-offset': designTokens.colors.background.surface,
      width: designTokens.sizes.width.radio,
      height: designTokens.sizes.height.radio,
      minWidth: designTokens.sizes.width.radio,
      minHeight: designTokens.sizes.height.radio,
    }

    const labelStyle: CSSProperties = {
      color: designTokens.colors.text.graphite,
      fontFamily: designTokens.typography.fontFamilies.primary,
      ...typographyStyle(designTokens.typography.body.lgRelaxed),
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setUncontrolledChecked(event.target.checked)
      }
      onChange?.(event)
    }

    return (
      <label
        className={cn(
          'inline-flex items-center',
          disabled && 'cursor-not-allowed opacity-50',
          className
        )}
        style={{ gap }}
      >
        <RadioGroupItem
          ref={ref}
          className={cn('peer sr-only', inputClassName)}
          {...(isControlled ? { checked: isChecked } : { defaultChecked })}
          disabled={disabled}
          onChange={handleChange}
          {...props}
        />
        <span
          className={cn(
            'flex items-center justify-center transition-colors',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--control-ring)] peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[var(--control-ring-offset)]',
            indicatorClassName
          )}
          style={indicatorStyle}
        >
          {isChecked ? checkedIcon : uncheckedIcon}
        </span>
        {labelContent ? (
          <span className={cn('select-none', labelClassName)} style={labelStyle}>
            {labelContent}
          </span>
        ) : null}
      </label>
    )
  }
)
RadioField.displayName = 'RadioField'

export { RadioGroup, RadioGroupItem, RadioField }
